import { Inject, Injectable, Optional } from '@angular/core';
import { MAT_DATE_FORMATS, MatDateFormats, NativeDateAdapter } from '@angular/material/core';
import { format as dfFormat, parse as dfParse, isValid } from 'date-fns';

import { addTimezoneOffset } from '@blaze-shared/shared-util';

import { APP_DATE_FORMATS } from './custom-native-date-adapter';
import { DynamicFormatDate } from './dynamic-format-date';

@Injectable()
export class DateFnsAdapter extends NativeDateAdapter {
  @Optional() @Inject(MAT_DATE_FORMATS) private readonly dateFormats:
    | DynamicFormatDate
    | MatDateFormats;

  /**
   * Change dates in calendar from narrow to short ones.
   * Fr -> Fri
   * @param style
   */
  override getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    if (style === 'narrow') {
      return super.getDayOfWeekNames('short');
    }
    return super.getDayOfWeekNames(style);
  }

  override format(date: Date, displayFormat: any): string {
    if (!isValid(date)) {
      return '';
    }

    const formats = this.dateFormats ?? APP_DATE_FORMATS;
    let pattern: string | undefined;

    if (typeof displayFormat === 'string') {
      // common keys used by MatDateFormats.display
      if (displayFormat === 'dateInput' || displayFormat === 'input') {
        pattern = formats.display.dateInput as string;
      } else if (displayFormat === 'monthYearLabel') {
        pattern = formats.display.monthYearLabel as string;
      } else if (displayFormat === 'dateA11yLabel') {
        pattern = formats.display.dateA11yLabel as string;
      } else if (displayFormat === 'monthYearA11yLabel') {
        pattern = formats.display.monthYearA11yLabel as string;
      } else {
        // caller passed a custom pattern string
        pattern = displayFormat;
      }
    } else {
      // fallback to configured dateInput pattern
      pattern = formats.display.dateInput as string;
    }

    try {
      return dfFormat(addTimezoneOffset(date), pattern);
    } catch {
      console.error('DateFnsAdapter: Error formatting date with pattern', pattern, date);
      return '';
    }
  }

  override parse(value: any): Date | null {
    if (!value && value !== 0) {
      return null;
    }
    if (value instanceof Date) {
      return isValid(value) ? value : null;
    }

    const formats = this.dateFormats ?? APP_DATE_FORMATS;
    const pattern = (formats.parse && (formats.parse.dateInput as string)) ?? 'yyyy-MM-dd';

    if (typeof value !== 'string') {
      const coerced = new Date(value);
      return isValid(coerced) ? coerced : null;
    }

    // try parse with configured pattern
    try {
      const parsed = dfParse(value, pattern, new Date());
      if (isValid(parsed)) {
        return parsed;
      }
    } catch {
      console.error('DateFnsAdapter: Error parsing date with pattern', pattern, value);
    }

    const nativeParsed = new Date(value);
    return isValid(nativeParsed) ? nativeParsed : null;
  }
}
